import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { List, Search, ShoppingCart, User, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { OrderSummary } from "./checkout/OrderSummary";
import { PromoBanner } from "./PromoBanner";
import { useCart } from "@/hooks/use-cart";
import { getProductImageUrl } from "@/utils/imageUtils";
// Import FontAwesome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBowlFood, faHeartPulse, faBone, faDog, faShieldDog, faList } from "@fortawesome/free-solid-svg-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import householdIcon from "@/assets/household-icon.png";
import outdoorGardenIcon from "@/assets/outdoor-garden-icon.png";
import blogIcon from "@/assets/blog-icon.png";
import customToysIcon from "@/assets/custom-toys-icon.png";
import k9chewsLogo from "@/assets/k9chews-logo.png";
import k9washAd from "@/assets/k9wash-ad.png.asset.json";

// Define a mapping for icons based on category simple_id or name
const categoryIconMap: { [key: number]: any; [key: string]: any } = {
  6: faBowlFood, // Food & Treats
  4: faHeartPulse, // Health
  3: faShieldDog, // Training
  25: faDog, // Gear (Accessories)
  5: faBone, // Toys
  1: faList, // All Products
  46: faBone, // Natural Treats
  57: faHeartPulse, // Calming Treats
  49: "household", // Household - custom image
  55: "outdoor-garden", // Outdoor & Garden - custom image
  "All Products": faList,
  "Natural Treats": faBone,
  "Calming Treats": faHeartPulse,
  // Add fallback or default icon if needed
  default: faList,
};

// Custom image mapping
const customCategoryImages: { [key: string]: string } = {
  household: householdIcon,
  "outdoor-garden": outdoorGardenIcon,
  blog: blogIcon,
  "custom-toys": customToysIcon,
};

// Function to get the appropriate icon
const getCategoryIcon = (category: { simple_id: number; name: string }) => {
  return categoryIconMap[category.simple_id] || categoryIconMap[category.name] || categoryIconMap["default"];
};

// Function to render category icon (handles both FontAwesome and custom images)
const renderCategoryIcon = (category: { simple_id: number; name: string }) => {
  const iconOrKey = getCategoryIcon(category);

  if (typeof iconOrKey === "string" && customCategoryImages[iconOrKey]) {
    return (
      <img
        src={customCategoryImages[iconOrKey]}
        alt={`${category.name} icon`}
        className="h-5 w-5 flex-shrink-0 object-contain"
        style={{
          filter:
            "brightness(0) saturate(100%) invert(36%) sepia(88%) saturate(1100%) hue-rotate(15deg) brightness(92%) contrast(96%)",
        }}
      />
    );
  }

  return <FontAwesomeIcon icon={iconOrKey} className="text-primary-deep h-5 w-5 flex-shrink-0" />;
};

export const Navbar = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const session = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [subtotal, setSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [total, setTotal] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string>("");
  const location = useLocation();

  // Use the cart hook instead of duplicate logic
  const { items: cartItems, removeItem } = useCart();

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        // Only update local cache/state here; avoid async Supabase calls
        queryClient.invalidateQueries({ queryKey: ["cart-items"] });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const { data: searchResults } = useQuery({
    queryKey: ["search-products", searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return [];

      const { data, error } = await supabase
        .from("products")
        .select(
          `
          *,
          product_images (
            image_url,
            is_primary
          )
        `,
        )
        .eq("is_hidden", false)
        .ilike("name", `%${searchQuery}%`)
        .limit(5);

      if (error) throw error;

      return data.map((product) => ({
        ...product,
        image_url:
          product.product_images?.find((img) => img.is_primary)?.image_url || product.image_url || "/placeholder.svg",
      }));
    },
    enabled: searchQuery.trim().length > 0,
  });

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      // First, fetch All Products category
      const { data: allProductsCategory, error: allProductsError } = await supabase
        .from("categories")
        .select("*")
        .eq("name", "All Products")
        .single();

      if (allProductsError) {
        console.error("Error fetching All Products category:", allProductsError);
      }

      // Then fetch other categories
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .is("parent_id", null)
        .neq("name", "All Products")
        .order("display_order", { ascending: true });

      if (error) throw error;

      // Combine the results with All Products first
      const combinedCategories = allProductsCategory ? [allProductsCategory, ...(data || [])] : data || [];

      // Filter out duplicates based on simple_id
      const uniqueCategories = combinedCategories.reduce((acc, current) => {
        const x = acc.find((item) => item.simple_id === current.simple_id);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);

      return uniqueCategories;
    },
  });

  useEffect(() => {
    if (cartItems) {
      console.log("Navbar: Setting cart items state with", cartItems.length, "items");
      const subtotalValue = cartItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
      setSubtotal(subtotalValue);
      setShippingCost(subtotalValue >= 30 ? 0 : 4.99);
      setTotal(subtotalValue + shippingCost);
    }
  }, [cartItems, shippingCost]);

  const removeFromCart = async (itemId: string) => {
    try {
      console.log("Navbar: Removing item from cart:", itemId);

      // Find the product ID from the item
      const item = cartItems.find((cartItem) => cartItem.id === itemId);
      if (!item) return;

      await removeItem(item.product.id);

      toast({
        title: "Item removed",
        description: "Item has been removed from your cart",
      });
    } catch (error) {
      console.error("Navbar: Error removing item from cart:", error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (session?.user?.id) {
        const { data, error } = await supabase.from("profiles").select("is_admin").eq("id", session.user.id).single();

        if (!error && data) {
          setIsAdmin(data.is_admin || false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [session]);

  const handleCheckout = () => {
    navigate("/cart");
  };

  // Fetch active advertisements for navbar
  const { data: navbarAdvertisements } = useQuery({
    queryKey: ["navbar-advertisements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("advertisements")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-warm-100/50 shadow-sm">
        <div className="container mx-auto mobile-padding">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Left section - Categories menu with advertisements */}
            <div className="flex-1 flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Open categories menu"
                    className="hover:bg-primary/10 transition-all duration-200 touch-target group rounded-full"
                  >
                    <List className="h-6 w-6 md:h-[50px] md:w-[50px] text-primary-deep group-hover:scale-110 transition-transform duration-200" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[400px] sm:w-[540px]">
                  <SheetHeader>
                    <SheetTitle>Categories</SheetTitle>
                  </SheetHeader>
                  <div className="py-6">
                    {/* Categories */}
                    {categories?.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.simple_id}`}
                        className="flex items-center gap-3 px-4 py-3 text-base hover:bg-gray-100 rounded-md transition-colors"
                      >
                        {renderCategoryIcon(category)}
                        <span>{category.name}</span>
                      </Link>
                    ))}

                    {/* Blog Link */}
                    <Link
                      to="/blog"
                      className="flex items-center gap-3 px-4 py-3 text-base hover:bg-gray-100 rounded-md transition-colors border-t border-gray-200 mt-2 pt-4"
                    >
                      <img
                        src={blogIcon}
                        alt="Blog and pet care tips"
                        className="h-5 w-5 flex-shrink-0 object-contain"
                        style={{
                          filter:
                            "brightness(0) saturate(100%) invert(36%) sepia(88%) saturate(1100%) hue-rotate(15deg) brightness(92%) contrast(96%)",
                        }}
                      />
                      <span>Blog & Pet Care Tips</span>
                    </Link>

                    {/* Custom Toys Link */}
                    <a
                      href="https://homeofblu.com/partner"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 text-base hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <img
                        src={customToysIcon}
                        alt="Custom dog toys for business"
                        className="h-5 w-5 flex-shrink-0 object-contain"
                        style={{
                          filter:
                            "brightness(0) saturate(100%) invert(36%) sepia(88%) saturate(1100%) hue-rotate(15deg) brightness(92%) contrast(96%)",
                        }}
                      />
                      <span>Custom Dog Toys (B2B)</span>
                    </a>


                    {/* Advertisements / sister-brand advert */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3 px-4">
                        Advertisement
                      </h4>

                      <a
                        href="https://k9wash.com"
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="block mx-4 mb-4 group overflow-hidden rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <img
                          src={k9washAd.url}
                          alt="K9Wash - find dog washes near you"
                          loading="lazy"
                          className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                        />
                        <div className="flex items-center justify-between gap-2 bg-gray-950 px-4 py-3">
                          <span className="text-xs text-white/80">From our sister brand K9Wash</span>
                          <span className="text-xs font-semibold text-primary whitespace-nowrap">Find Now →</span>
                        </div>
                      </a>

                      {navbarAdvertisements?.map((ad) => (
                        <Link key={ad.id} to={ad.link_url || "#"} className="block mx-4 mb-4 group">
                          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            {ad.image_url && (
                              <div className="w-full aspect-[4/3] bg-white overflow-hidden">
                                <img
                                  src={getProductImageUrl(ad.image_url)}
                                  alt={ad.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                />
                              </div>
                            )}
                            <div className="bg-gray-950 px-4 py-3">
                              <h5 className="text-sm font-semibold text-white mb-1">{ad.title}</h5>
                              {ad.description && (
                                <p className="text-xs text-white/70 line-clamp-2 mb-1">{ad.description}</p>
                              )}
                              <span className="text-xs font-semibold text-primary">Learn More →</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Search Sheet */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Search products"
                    className="hover:bg-primary/10 transition-all duration-200 touch-target group rounded-full"
                  >
                    <Search className="h-6 w-6 md:h-[50px] md:w-[50px] text-primary-deep group-hover:scale-110 transition-transform duration-200" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[400px] sm:w-[540px]">
                  <SheetHeader>
                    <SheetTitle>Search Products</SheetTitle>
                  </SheetHeader>
                  <div className="py-4">
                    <Input
                      placeholder="Search for treats, toys, food & more..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="mb-4"
                    />
                    <ScrollArea className="h-[calc(100vh-12rem)]">
                      <div className="space-y-4">
                        {searchResults?.map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${product.id}`}
                            className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            onClick={() => setSearchQuery("")}
                          >
                            <img
                              src={getProductImageUrl(product.image_url)}
                              alt={product.name}
                              className="w-16 h-16 object-cover rounded"
                            />
                            <div>
                              <h3 className="font-medium">{product.name}</h3>
                              <p className="text-sm text-gray-500">{product.brand}</p>
                              <p className="text-sm font-medium">£{product.price}</p>
                            </div>
                          </Link>
                        ))}
                        {searchQuery && searchResults?.length === 0 && (
                          <p className="text-center text-gray-500">No products found</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Center section - Logo */}
            <div className="flex-1 flex justify-center">
              <Link to="/" className="flex items-center justify-center" onClick={handleLogoClick}>
                <img
                  src={k9chewsLogo}
                  alt="K9Chews Pet Supplies"
                  className="h-10 md:h-[62px] w-auto object-contain transition-transform duration-200 hover:scale-105"
                />
              </Link>
            </div>

            {/* Right section - updated cart logic */}
            <div className="flex-1 flex items-center justify-end gap-2">
              {/* Cart Sheet - updated to support guest checkout */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Open shopping cart"
                    className="relative hover:bg-primary/10 transition-all duration-200 touch-target group rounded-full"
                  >
                    <ShoppingCart className="h-6 w-6 md:h-[50px] md:w-[50px] text-primary-deep hover:scale-110 transition-transform duration-200 cart-icon" />
                    {cartItems.length > 0 && (
                      <Badge
                        className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
                        variant="default"
                      >
                        {cartItems.reduce((total, item) => total + item.quantity, 0)}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:w-[540px] flex flex-col h-full">
                  <SheetHeader>
                    <SheetTitle>Your Cart</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col h-[calc(100vh-6rem)]">
                    <div className="flex-1 overflow-hidden">
                      {cartItems.length > 0 ? (
                        <ScrollArea className="h-[calc(100vh-15rem)]">
                          <div className="space-y-4 pr-4">
                            {cartItems.map((item) => (
                              <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg relative">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Remove item from cart"
                                  className="absolute top-2 right-2 h-6 w-6"
                                  onClick={() => removeFromCart(item.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <img
                                  src={getProductImageUrl(item.product.image_url)}
                                  alt={item.product.name}
                                  className="w-20 h-20 object-cover rounded"
                                />
                                <div className="flex-1">
                                  <h3 className="font-medium">{item.product.name}</h3>
                                  <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                                  <p className="text-sm font-medium">
                                    £{(item.product.price * item.quantity).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      ) : (
                        <p className="text-center text-gray-500">Your cart is empty</p>
                      )}
                    </div>
                    {cartItems.length > 0 && (
                      <div className="border-t pt-4 mt-auto bg-white">
                        <OrderSummary
                          cartItems={cartItems}
                          subtotal={subtotal}
                          shippingCost={shippingCost}
                          total={total}
                          onCouponApply={(code, discount) => {
                            setDiscountPercentage(discount);
                            setAppliedCouponCode(code);
                          }}
                        />
                        <Button className="w-full" onClick={handleCheckout}>
                          View Cart & Checkout
                        </Button>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              {/* Profile Button */}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Account and profile"
                className="hover:bg-gray-100 transition-colors duration-200 touch-target"
                onClick={() => navigate(session ? "/profile" : "/customer/login")}
              >
                <User className="h-6 w-6 md:h-[50px] md:w-[50px] text-primary-deep group-hover:scale-110 transition-transform duration-200" />
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-20">
        {/* PromoBanner is now only rendered here, not in Index.tsx */}
        <PromoBanner />
      </div>
    </>
  );
};
